import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getGroupIdFromUrl } from '../../utils/getGroupIdFromUrl';
import { buildLiteJoinUrl } from '../../utils/lite-join-url';
import { useNavigateWithGroupId } from '../../hooks/useNavigateWithGroupId';
import { getLiteJwtTokenFromUrlOrStorage } from '../../utils/liteJwtToken';
import { useLiff } from '../../contexts/LiffContext';
import {
  checkGroupTenant,
  getPrepaidList,
  type PrepaidListItem,
} from '../../api/contextClient';
import { getSubscriptionStatus } from '../../api/liteSubscriptionClient';
import { clearLiteJwtToken } from '../../api/liteAuthToken';
import {
  getActiveAnnouncements,
  type Announcement,
} from '../../api/liteAnnouncementsClient';
import { utcToTaipei, DAY_MS } from '@pos/shared/utils/time';
import { isLitePrepaidShopType } from '@pos/shared';
import { isLitePaidPlan } from '../../utils/liteSubscription';
import {
  litePathSubscription,
  litePathSubscriptionCancel,
  litePathSetting,
  litePathHold,
  litePathDeliver,
  litePathDeliverResult,
} from '../../utils/liteRoutes';
import { Button } from '@/components/ui/button';

import { StoreDashboardView } from '@/components/StoreDashboardView';
import { ActivityDeliveryBlock } from '@/components/ActivityDeliveryBlock';
import { MerchantSubscriptionBlock } from '@/components/MerchantSubscriptionBlock';
import { PrepaidListBlock } from '@/components/PrepaidListBlock';
import { LITE_SOCIAL_LINKS } from '@/constants/social';

/**
 * Lite 主頁面
 * 註冊和登入後都會導向此頁面
 * 顯示商家資訊與活動交付列表
 */
export function MainPage() {
  const navigate = useNavigate();
  const { liff, isLoggedIn } = useLiff();

  // 取得當前 groupId，用於路由跳轉時保留
  const groupId = getGroupIdFromUrl();
  const navigateWithGroupId = useNavigateWithGroupId();

  /** 訂閱狀態：目前方案、已使用／上限、預計更新日、是否已設不續訂（用於商家區塊內） */
  const [subStatus, setSubStatus] = useState<{
    planSlug: 'free' | 'trial' | 'basic' | 'plus' | 'pro';
    used: number;
    limit: number;
    periodEnd: string | null;
    cancelAtPeriodEnd?: boolean;
    isTrial: boolean;
    trialEndsAt: string | null;
  } | null>(null);
  const [subStatusLoading, setSubStatusLoading] = useState(false);

  // 公告相關狀態
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [currentAnnouncementIndex, setCurrentAnnouncementIndex] = useState(0);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);

  const [tenantInfo, setTenantInfo] = useState<{
    name: string;
    type: string | null;
    /** 統一商家 URL suffix（有值時 join URL 用 /lite/shop/:suffix/join/:id） */
    suffix: string | null;
  } | null>(null);
  const [isTenantLoading, setIsTenantLoading] = useState(true);

  const getJwtToken = (): string | null => getLiteJwtTokenFromUrlOrStorage();

  // 載入公告
  useEffect(() => {
    const loadAnnouncements = async () => {
      try {
        const activeAnnouncements = await getActiveAnnouncements();
        if (activeAnnouncements.length > 0) {
          setAnnouncements(activeAnnouncements);
          setCurrentAnnouncementIndex(0);
          setShowAnnouncementModal(true);
        }
      } catch (error) {
        console.warn('[MainPage] 載入公告失敗:', error);
        // 公告載入失敗不影響主要功能，只記錄警告
      }
    };
    loadAnnouncements();
  }, []);

  // 載入商家資訊（只讀顯示）
  useEffect(() => {
    const loadTenantInfo = async () => {
      if (!groupId) {
        setTenantInfo(null);
        setIsTenantLoading(false);
        return;
      }

      setIsTenantLoading(true);
      try {
        let lineIdToken: string | undefined;
        if (liff && isLoggedIn) {
          try {
            const token = await liff.getIDToken();
            lineIdToken = token || undefined;
          } catch (error) {
            console.warn('[MainPage] 無法取得 LINE ID Token:', error);
          }
        }

        const result = await checkGroupTenant(groupId, lineIdToken);
        if (result.hasTenant && result.tenant) {
          setTenantInfo({
            name: result.tenant.name || '',
            type: result.tenant.type || null,
            suffix: result.tenant.suffix ?? null,
          });
        } else {
          setTenantInfo(null);
        }
      } catch (error) {
        console.error('[MainPage] 載入商家資訊失敗:', error);
        toast.error('載入商家資訊失敗');
        setTenantInfo(null);
      } finally {
        setIsTenantLoading(false);
      }
    };

    loadTenantInfo();
  }, [groupId, liff, isLoggedIn]);

  const fetchSubStatus = useCallback(() => {
    if (!groupId) return;
    const jwt = getJwtToken();
    if (!jwt) return;
    setSubStatusLoading(true);
    getSubscriptionStatus(jwt, undefined, { groupId })
      .then(setSubStatus)
      .catch(() => setSubStatus(null))
      .finally(() => setSubStatusLoading(false));
  }, [groupId]);

  useEffect(() => {
    if (groupId && getJwtToken()) {
      fetchSubStatus();
    } else {
      setSubStatus(null);
      setSubStatusLoading(false);
    }
  }, [groupId, fetchSubStatus]);

  const trialDaysLeft = useMemo(() => {
    if (
      !subStatus?.isTrial ||
      !subStatus.trialEndsAt ||
      isLitePaidPlan(subStatus.planSlug)
    ) {
      return null;
    }
    const nowTaipei = new Date(utcToTaipei(new Date().toISOString()));
    const trialEndTaipei = new Date(utcToTaipei(subStatus.trialEndsAt));
    const diffMs = trialEndTaipei.getTime() - nowTaipei.getTime();
    return Math.max(0, Math.ceil(diffMs / DAY_MS));
  }, [subStatus]);

  // 活動交付區塊（與 Square 共用：可收合、統計、結算、導向、分享）
  const activityDeliveryContent = (
    <ActivityDeliveryBlock
      mode="group"
      groupId={groupId}
      onCreateActivity={() => navigateWithGroupId(litePathHold())}
      onNavigateToDeliver={id => navigateWithGroupId(litePathDeliver(id))}
      onNavigateToResult={id => navigateWithGroupId(litePathDeliverResult(id))}
      getActivityJoinUrl={
        groupId
          ? id =>
              buildLiteJoinUrl({
                origin:
                  typeof window !== 'undefined' ? window.location.origin : '',
                suffix: tenantInfo?.suffix ?? undefined,
                groupId: tenantInfo?.suffix ? undefined : groupId,
                activityId: id,
              })
          : undefined
      }
    />
  );

  // Group 專用：商家資訊卡右上角按鈕、目前方案區塊
  const merchantExtraButtons = (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => navigateWithGroupId(litePathSubscription())}
      >
        訂閱方案
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => navigateWithGroupId(litePathSetting())}
      >
        前往設定
      </Button>
    </>
  );
  const merchantSubscriptionBlock =
    subStatusLoading || subStatus ? (
      <MerchantSubscriptionBlock
        loading={subStatusLoading}
        subStatus={subStatus}
        onGoToSubscription={() => navigateWithGroupId(litePathSubscription())}
        onOpenCancel={() => navigateWithGroupId(litePathSubscriptionCancel())}
      />
    ) : null;

  const merchantStatusMessage = !groupId ? (
    <p className="text-muted-foreground">缺少 groupId，請從 LINE 群組開啟。</p>
  ) : isTenantLoading ? (
    <p className="text-muted-foreground">載入中...</p>
  ) : !tenantInfo ? (
    <p className="text-muted-foreground">尚未設定商家資訊。</p>
  ) : undefined;

  /** 預繳名單（名稱、次數、性別、最近更新） */
  const [prepaidList, setPrepaidList] = useState<PrepaidListItem[]>([]);
  const [prepaidListLoading, setPrepaidListLoading] = useState(false);
  useEffect(() => {
    if (!groupId) {
      setPrepaidList([]);
      return;
    }
    const jwt = getJwtToken();
    if (!jwt) {
      setPrepaidList([]);
      return;
    }
    let cancelled = false;
    setPrepaidListLoading(true);
    getPrepaidList(jwt)
      .then(list => {
        if (!cancelled) setPrepaidList(list);
      })
      .catch(() => {
        if (!cancelled) setPrepaidList([]);
      })
      .finally(() => {
        if (!cancelled) setPrepaidListLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [groupId]);

  const merchantPrepaidBlock = isLitePrepaidShopType(tenantInfo?.type) ? (
    <PrepaidListBlock
      loading={prepaidListLoading}
      items={prepaidList}
      emptyMessage="尚無預繳名單"
    />
  ) : undefined;

  return (
    <>
      <StoreDashboardView
        storeName={tenantInfo?.name ?? '（未設定名稱）'}
        storeType={tenantInfo?.type ?? null}
        socialLinks={LITE_SOCIAL_LINKS}
        activityDeliveryContent={activityDeliveryContent}
        merchantExtraButtons={merchantExtraButtons}
        merchantSubscriptionBlock={merchantSubscriptionBlock}
        merchantStatusMessage={merchantStatusMessage}
        merchantPrepaidBlock={merchantPrepaidBlock}
        onCreateActivity={() => navigateWithGroupId(litePathHold())}
      />

      {/* 公告 Modal */}
      {showAnnouncementModal && announcements.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6 space-y-4">
            <div className="flex justify-between items-start">
              <h3 className="text-lg font-semibold text-gray-900">公告</h3>
              <button
                onClick={() => {
                  if (currentAnnouncementIndex < announcements.length - 1) {
                    setCurrentAnnouncementIndex(currentAnnouncementIndex + 1);
                  } else {
                    setShowAnnouncementModal(false);
                  }
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                {currentAnnouncementIndex < announcements.length - 1
                  ? '下一則'
                  : '關閉'}
              </button>
            </div>
            <div className="text-gray-700 whitespace-pre-wrap">
              {announcements[currentAnnouncementIndex]?.content}
            </div>
            {announcements.length > 1 && (
              <div className="text-sm text-gray-500 text-center">
                {currentAnnouncementIndex + 1} / {announcements.length}
              </div>
            )}
          </div>
        </div>
      )}

      {import.meta.env.DEV && (
        <div className="fixed bottom-3 right-3 z-40">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              clearLiteJwtToken();
              if (typeof window !== 'undefined') {
                window.location.href = '/lite';
              }
            }}
          >
            重置 Lite JWT（dev）
          </Button>
        </div>
      )}
    </>
  );
}
